import React, { FC } from "react"
import PropTypes from "prop-types"
import { ArrowLink } from "../views/atoms/links/ArrowLink"
import CarrotMarkdown from "../../utils/CarrotMarkdown"
import styled from "styled-components"
import { useSelector } from "react-redux"
import { getCompanyName } from "../../reducers/companyInfo"
import { getReimbursementPercent } from "../../reducers/subsidyStatus"
import { getYearlyMax } from "../../reducers/subsidyStatus"
import { getLifetimeMax } from "../../reducers/subsidyStatus"
import { CurrencyString } from "../views/molecules/CurrencyString"
import { CustomCoverage } from "./CustomCoverage"
import { getBenefitConfiguration } from "../../reducers/benefitConfiguration"
import { TextLink } from "components/views/atoms/links/TextLink"
import { getAvaCheckoutLandingPageUrl } from "../../reducers/ava"

const DynamicH2 = styled.h2`
  font-family: Publico Headline;
  font-style: normal;
  font-weight: bold;
  font-size: 24px;
  line-height: 140%;
  color: rgba(0, 0, 0, 0.8);
  margin-top: 0;
  margin-bottom: 4px;
`

const DynamicH3 = styled.h3`
  font-family: "Gotham A", "Gotham B", sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 150%;
  color: rgba(0, 0, 0, 0.8);
  margin-top: 24px;
  margin-bottom: 0;
`

const DynamicH5 = styled.h5`
  font-family: "Gotham A", "Gotham B", sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 21px;
  margin: 0;
  color: rgba(0, 0, 0, 0.8);
`

const DynamicP = styled.p`
  margin-top: 0;
  margin-bottom: 24px;
  line-height: 150%;
  font-family: "Gotham A", "Gotham B", sans-serif;
  color: rgba(0, 0, 0, 0.8);
`

const DynamicHr = styled.hr`
  margin: 32px 0 32px 0;
  border: none;
  border-bottom: rgba(0, 0, 0, 0.1) solid 1px;
`

const DynamicUl = styled.ul`
  font-size: 14px;
  font-family: "Gotham A", "Gotham B", sans-serif;
  font-style: normal;
  font-weight: 400;
  margin-top: 0;
  margin-bottom: 0;
`

const DynamicOl = styled.ol`
  margin-left: -24px;
  font-weight: 500;
  padding-inline-start: 40px;
  list-style: decimal;
  li {
    margin-bottom: 24px;
    &:last-child {
      margin-bottom: 0;
    }
  }
`

const DynamicLi = styled.li`
  margin-bottom: 12px;
  line-height: 150%;
  color: rgba(0, 0, 0, 0.8);
  &:last-child {
    margin-bottom: 0;
  }
`

const OlBody = styled.p`
  margin-top: 0;
  margin-bottom: 12px;
  line-height: 150%;
  font-weight: 400;
  color: rgba(0, 0, 0, 0.8);
`

const DynamicBodySmall = styled.p`
  // currently only used in paying for care
  // margin-top set to -20px to collapse margin under blockquote
  margin-top: -20px;
  margin-bottom: 14px;
  font-size: 14px;
  line-height: 150%;
  font-family: "Gotham A", "Gotham B", sans-serif;
  color: rgba(0, 0, 0, 0.6);
`

const BlockQuoteCustomComponentBody = styled.div`
  font-size: 16px;
  margin-top: 0;
  margin-bottom: 8px;
  line-height: 150%;
  font-family: "Gotham A", "Gotham B", sans-serif;
  color: rgba(0, 0, 0, 0.8);
  &:last-child {
    margin-bottom: 0;
  }
`

const BlockQuote = styled.blockquote`
  margin: 0;
`

const DynamicBlockQuoteWrapper = styled.div`
  padding: 24px;
  border-radius: 8px;
  width: 100%;
  background-color: #faf9f7;
  margin: 16px 0 24px 0;
  display: flex;
  p {
    font-family: "Gotham A", "Gotham B", sans-serif;
    margin-top: 0;
    margin-bottom: 0;
    font-size: 14px;
    line-height: 150%;
    color: rgba(0, 0, 0, 0.8);
    &:last-child {
      margin-bottom: 0;
    }
  }
`

export const DynamicBlockQuote = ({ children }) => (
  <DynamicBlockQuoteWrapper>
    <BlockQuote>{children}</BlockQuote>
  </DynamicBlockQuoteWrapper>
)
/* eslint-disable */
const dynamicContentStyleOverrides = {
  h2: DynamicH2,
  h3: DynamicH3,
  h5: DynamicH5,
  p: DynamicP,
  hr: DynamicHr,
  ul: DynamicUl,
  ol: DynamicOl,
  li: DynamicLi,
  OlBody: OlBody,
  BodySmall: DynamicBodySmall,
  CompanyName: () => {
    const companyNameInfo = useSelector(getCompanyName)
    return <span>{companyNameInfo}</span>
  },
  CostSharePercentage: () => {
    const reimbursementPercentInfo = useSelector(getReimbursementPercent)
    return (
      <BlockQuoteCustomComponentBody>{reimbursementPercentInfo}% of eligible expenses</BlockQuoteCustomComponentBody>
    )
  },
  YearlyBenefit: () => {
    const yearlyBenefit = useSelector(getYearlyMax)
    return (
      <BlockQuoteCustomComponentBody>
        <CurrencyString amount={yearlyBenefit} /> per benefit year
      </BlockQuoteCustomComponentBody>
    )
  },
  LifetimeMax: () => {
    const lifetimeMax = useSelector(getLifetimeMax)
    return (
      <BlockQuoteCustomComponentBody>
        <CurrencyString amount={lifetimeMax} /> lifetime maximum
      </BlockQuoteCustomComponentBody>
    )
  },
  CustomCoverage: () => {
    const benefitConfiguration = useSelector(getBenefitConfiguration)
    if (!benefitConfiguration) return null
    const { customCoverageText } = benefitConfiguration

    return <CustomCoverage customCoverageText={customCoverageText} />
  },
  ArrowLink: ({ children, href }) => (
    <ArrowLink to={href} target="_blank">
      {children}
    </ArrowLink>
  ),
  AvaArrowLink: ({ children }) => {
    const avaCheckoutLandingPageUrl = useSelector(getAvaCheckoutLandingPageUrl)
    return (
      <ArrowLink to={avaCheckoutLandingPageUrl} target="_blank">
        {children}
      </ArrowLink>
    )
  },
  blockquote: ({ children }) => <DynamicBlockQuote>{children}</DynamicBlockQuote>,
  a({ href, title, target = "_blank", children }) {
    return (
      <TextLink target={target} title={title} to={href}>
        {children}
      </TextLink>
    )
  }
}

type DynamicContentMarkdownFields = {
  additionalClasses?: string
  styleOverrides?: object
  source: string
}

const DynamicContentMarkdown: FC<DynamicContentMarkdownFields> = ({
  additionalClasses = "",
  source,
  styleOverrides = dynamicContentStyleOverrides
}) => (
  <div className={additionalClasses}>
    <CarrotMarkdown source={source} styleOverrides={styleOverrides} />
  </div>
)

DynamicContentMarkdown.propTypes = {
  source: PropTypes.string.isRequired,
  additionalClasses: PropTypes.string
}

const getStyleOverridesClone = () => ({ ...dynamicContentStyleOverrides })

export { DynamicContentMarkdown, getStyleOverridesClone }
